import React, { useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { Toaster } from './components/ui/sonner';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import QuizPools from './pages/QuizPools';
import ActiveQuiz from './pages/ActiveQuiz';
import Leaderboard from './pages/Leaderboard';
import Rewards from './pages/Rewards';
import BuyPoints from './pages/BuyPoints';
import MyContests from './pages/MyContests';
import GlobalLeaderboard from './pages/GlobalLeaderboard';
import StateLeaderboard from './pages/StateLeaderboard';
import Referral from './pages/Referral';
import AdminDashboard from './pages/AdminDashboard';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import QuizResults from './pages/QuizResults';
import PracticeQuiz from './pages/PracticeQuiz';
import Profile from './pages/Profile';
import VerifyEmail from './pages/VerifyEmail';
import VerifyOtp from './pages/VerifyOtp';
import ForInstitutes from './pages/ForInstitutes';
import Upgrade from './pages/Upgrade';
import MockTests from './pages/MockTests';
import CollegePredictor from './pages/CollegePredictor';
import DoubtSolver from './pages/DoubtSolver';
import Institutes from './pages/Institutes';
import ProLeaderboard from './pages/ProLeaderboard';
import DailyMocks from './pages/DailyMocks';
import ActiveMockTest from './pages/ActiveMockTest';
import HeadToHead from './pages/HeadToHead';
import ActiveH2H from './pages/ActiveH2H';
import Courses from './pages/Courses';
import CourseDetail from './pages/CourseDetail';
import CourseChapter from './pages/CourseChapter';
import PerformanceReport from './pages/PerformanceReport';
import CrashCourse from './pages/CrashCourse';

const PUBLIC_LIGHT_ROUTES = new Set([
  '/register', '/forgot-password',
  '/terms', '/privacy', '/verify-email', '/verify-otp', '/for-institutes',
]);

function AppRouter() {
  const location = useLocation();
  const { theme } = useTheme();

  // Central DOM class control: public/auth pages force light, protected pages use theme.
  useEffect(() => {
    const html = document.documentElement;
    const isPublic = PUBLIC_LIGHT_ROUTES.has(location.pathname);
    const effective = isPublic ? 'light' : theme;
    html.classList.remove('dark', 'light');
    html.classList.add(effective);
  }, [location.pathname, theme]);

  // Prime the class synchronously on mount to avoid flash
  if (typeof document !== 'undefined') {
    const html = document.documentElement;
    const isPublic = PUBLIC_LIGHT_ROUTES.has(location.pathname);
    const effective = isPublic ? 'light' : theme;
    if (!html.classList.contains(effective)) {
      html.classList.remove('dark', 'light');
      html.classList.add(effective);
    }
  }

  // CRITICAL: Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/verify-otp" element={<VerifyOtp />} />
      <Route path="/for-institutes" element={<ForInstitutes />} />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/quiz-pools"
        element={
          <ProtectedRoute>
            <QuizPools />
          </ProtectedRoute>
        }
      />

      <Route
        path="/quiz/:poolId"
        element={
          <ProtectedRoute>
            <ActiveQuiz />
          </ProtectedRoute>
        }
      />

      <Route
        path="/quiz-results/:poolId"
        element={
          <ProtectedRoute>
            <QuizResults />
          </ProtectedRoute>
        }
      />

      <Route
        path="/leaderboard/:poolId"
        element={
          <ProtectedRoute>
            <Leaderboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/rewards"
        element={
          <ProtectedRoute>
            <Rewards />
          </ProtectedRoute>
        }
      />

      <Route
        path="/buy-points"
        element={
          <ProtectedRoute>
            <BuyPoints />
          </ProtectedRoute>
        }
      />

      <Route
        path="/my-contests"
        element={
          <ProtectedRoute>
            <MyContests />
          </ProtectedRoute>
        }
      />

      <Route
        path="/global-leaderboard"
        element={
          <ProtectedRoute>
            <GlobalLeaderboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/leaderboard/state/:state"
        element={
          <ProtectedRoute>
            <StateLeaderboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/referral"
        element={
          <ProtectedRoute>
            <Referral />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminDashboard />
          </AdminRoute>
        }
      />

      <Route
        path="/practice"
        element={
          <ProtectedRoute>
            <PracticeQuiz />
          </ProtectedRoute>
        }
      />

      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />

      <Route
        path="/upgrade"
        element={
          <ProtectedRoute>
            <Upgrade />
          </ProtectedRoute>
        }
      />

      <Route
        path="/mock-tests"
        element={
          <ProtectedRoute>
            <MockTests />
          </ProtectedRoute>
        }
      />

      <Route
        path="/college-predictor"
        element={
          <ProtectedRoute>
            <CollegePredictor />
          </ProtectedRoute>
        }
      />

      <Route
        path="/doubt-solver"
        element={
          <ProtectedRoute>
            <DoubtSolver />
          </ProtectedRoute>
        }
      />

      <Route
        path="/institutes"
        element={
          <ProtectedRoute>
            <Institutes />
          </ProtectedRoute>
        }
      />

      <Route
        path="/pro-leaderboard"
        element={
          <ProtectedRoute>
            <ProLeaderboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/daily-mocks"
        element={
          <ProtectedRoute>
            <DailyMocks />
          </ProtectedRoute>
        }
      />

      <Route
        path="/mock/:mockId"
        element={
          <ProtectedRoute>
            <ActiveMockTest />
          </ProtectedRoute>
        }
      />

      <Route
        path="/h2h"
        element={
          <ProtectedRoute>
            <HeadToHead />
          </ProtectedRoute>
        }
      />

      <Route
        path="/h2h/match/:matchId"
        element={
          <ProtectedRoute>
            <ActiveH2H />
          </ProtectedRoute>
        }
      />

      <Route
        path="/courses"
        element={
          <ProtectedRoute>
            <Courses />
          </ProtectedRoute>
        }
      />

      <Route
        path="/courses/:courseId"
        element={
          <ProtectedRoute>
            <CourseDetail />
          </ProtectedRoute>
        }
      />

      <Route
        path="/courses/:courseId/learn/:chapterId"
        element={
          <ProtectedRoute>
            <CourseChapter />
          </ProtectedRoute>
        }
      />

      <Route
        path="/performance"
        element={
          <ProtectedRoute>
            <PerformanceReport />
          </ProtectedRoute>
        }
      />

      <Route
        path="/crash-course"
        element={
          <ProtectedRoute>
            <CrashCourse />
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="App">
            <AppRouter />
            <Toaster position="top-right" richColors theme="dark" />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
